import { type ReactElement, useEffect, useRef } from 'react'
import { useBlocker } from 'react-router'
import MaConfirm from './MaConfirm'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'

function UnsavedChangesConfirm ({
  unsavedChanges,
  message,
  confirmLabel
}: Readonly<{
  unsavedChanges: boolean
  message?: string
  confirmLabel?: string
}>): ReactElement {
  const { t } = useTranslation()
  const userUnloaded = useRef(false)
  const { events } = useAuth()

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      unsavedChanges && currentLocation.pathname !== nextLocation.pathname
  )

  useEffect(() => {
    const userUnloadedCallback = (): void => {
      userUnloaded.current = true
    }
    events.addUserUnloaded(userUnloadedCallback)
    return () => {
      events.removeUserUnloaded(userUnloadedCallback)
    }
  }, [events])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!userUnloaded.current && unsavedChanges) {
        event.preventDefault()
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [unsavedChanges])

  return (
    <MaConfirm
      show={blocker.state === 'blocked'}
      title={t('unsaved_changes.title')}
      variant="warning"
      confirmLabel={confirmLabel ?? t('common.button.labels.ok')}
      closeLabel={t('common.button.labels.cancel')}
      onConfirm={() => {
        blocker.proceed?.()
      }}
      onClose={() => {
        blocker.reset?.()
      }}
    >
      <p>{message ?? t('unsaved_changes.message')}</p>
    </MaConfirm>
  )
}

export default UnsavedChangesConfirm
